import { useEffect } from "react";
import { getLocalSeoSettings } from "../api";

/**
 * Updates a <meta> tag in document.head
 */
function updateMetaTag(attribute, attrValue, content) {
  if (typeof document === "undefined") return;
  let element = document.querySelector(`meta[${attribute}="${attrValue}"]`);
  if (!element) {
    element = document.createElement("meta");
    element.setAttribute(attribute, attrValue);
    document.head.appendChild(element);
  }
  element.setAttribute("content", content || "");
}

/**
 * Updates a <link> tag in document.head (e.g. canonical)
 */
function updateLinkTag(rel, href) {
  if (typeof document === "undefined") return;
  let element = document.querySelector(`link[rel="${rel}"]`);
  if (!element) {
    element = document.createElement("link");
    element.setAttribute("rel", rel);
    document.head.appendChild(element);
  }
  element.setAttribute("href", href || "");
}

/**
 * Universal SEO & Schema.org JSON-LD Component
 */
export default function SEO({
  title,
  description,
  canonicalUrl,
  ogType = "website",
  ogImage,
  noindex = false,
  schema = null,
}) {
  useEffect(() => {
    if (typeof document === "undefined") return;

    const settings = getLocalSeoSettings();
    const siteName = settings.siteName || "Mini Golf USA";
    const defaultSiteUrl = settings.siteUrl || "https://minigolfalabama.com";
    const defaultImg = settings.defaultOgImage || `${defaultSiteUrl}/logo.png`;
    const defaultDesc = settings.defaultDescription || "Find the best indoor and outdoor mini golf, putt putt, and glow golf courses in Alabama and across the USA.";

    // 1. Title
    const finalTitle = title
      ? title.includes("Mini Golf")
        ? title
        : `${title} | ${siteName}`
      : settings.defaultTitle || `Mini Golf Alabama | Best Indoor & Outdoor Mini Golf Near Me`;
    document.title = finalTitle;

    // 2. Standard Meta
    const finalDesc = description || defaultDesc;
    updateMetaTag("name", "description", finalDesc);
    updateMetaTag("name", "robots", noindex ? "noindex, nofollow" : "index, follow, max-image-preview:large");
    updateMetaTag("name", "google-site-verification", settings.googleSiteVerification || "bi4Dgit8tyad33aIxnsWe3v0F_ufmeXoIziR7ybwYjU");

    // 3. Canonical URL
    const finalUrl =
      canonicalUrl || (typeof window !== "undefined" ? window.location.href : defaultSiteUrl);
    updateLinkTag("canonical", finalUrl);

    // 4. Open Graph Meta Tags
    const finalImage = ogImage || defaultImg;
    updateMetaTag("property", "og:site_name", siteName);
    updateMetaTag("property", "og:type", ogType);
    updateMetaTag("property", "og:title", finalTitle);
    updateMetaTag("property", "og:description", finalDesc);
    updateMetaTag("property", "og:url", finalUrl);
    updateMetaTag("property", "og:image", finalImage);

    // 5. Twitter Card Meta Tags
    updateMetaTag("name", "twitter:card", "summary_large_image");
    updateMetaTag("name", "twitter:title", finalTitle);
    updateMetaTag("name", "twitter:description", finalDesc);
    updateMetaTag("name", "twitter:image", finalImage);

    // 6. JSON-LD Schema.org Structured Data
    const scriptId = "dynamic-seo-jsonld";
    let scriptTag = document.getElementById(scriptId);

    // Filter out disabled schema items
    let activeSchema = schema;
    if (Array.isArray(schema)) {
      activeSchema = schema.filter(Boolean);
      if (activeSchema.length === 0) activeSchema = null;
    }

    if (activeSchema) {
      if (!scriptTag) {
        scriptTag = document.createElement("script");
        scriptTag.id = scriptId;
        scriptTag.type = "application/ld+json";
        document.head.appendChild(scriptTag);
      }

      const schemaData = Array.isArray(activeSchema)
        ? {
            "@context": "https://schema.org",
            "@graph": activeSchema.map((s) => ({
              ...s,
              "@context": undefined,
            })),
          }
        : activeSchema["@context"]
        ? activeSchema
        : { "@context": "https://schema.org", ...activeSchema };

      scriptTag.textContent = JSON.stringify(schemaData);
    } else if (scriptTag) {
      scriptTag.remove();
    }

    // 7. Optional Global Custom JSON-LD from settings
    const customScriptId = "global-custom-jsonld";
    let customScriptTag = document.getElementById(customScriptId);
    if (settings.customHeadJsonLd && settings.customHeadJsonLd.trim().startsWith("{")) {
      if (!customScriptTag) {
        customScriptTag = document.createElement("script");
        customScriptTag.id = customScriptId;
        customScriptTag.type = "application/ld+json";
        document.head.appendChild(customScriptTag);
      }
      customScriptTag.textContent = settings.customHeadJsonLd;
    } else if (customScriptTag) {
      customScriptTag.remove();
    }

    // 8. Google Analytics (GA4 gtag.js) Tracking
    const gaId = (settings.googleAnalyticsId || "").trim();
    if (gaId && gaId.startsWith("G-")) {
      const gaScriptId = "google-analytics-gtag";
      let gaScript = document.getElementById(gaScriptId);
      if (!gaScript) {
        gaScript = document.createElement("script");
        gaScript.id = gaScriptId;
        gaScript.async = true;
        gaScript.src = `https://www.googletagmanager.com/gtag/js?id=${gaId}`;
        document.head.appendChild(gaScript);

        const inlineScript = document.createElement("script");
        inlineScript.id = "google-analytics-init";
        inlineScript.textContent = `
          window.dataLayer = window.dataLayer || [];
          function gtag(){dataLayer.push(arguments);}
          gtag('js', new Date());
          gtag('config', '${gaId}', { send_page_view: false });
        `;
        document.head.appendChild(inlineScript);
      }

      if (typeof window.gtag === "function") {
        window.gtag("event", "page_view", {
          page_title: finalTitle,
          page_location: finalUrl,
          page_path: typeof window !== "undefined" ? window.location.pathname + window.location.search : "/",
        });
      }
    }

    return () => {
      // Cleanup script on unmount
      const tag = document.getElementById(scriptId);
      if (tag) tag.remove();
      const customTag = document.getElementById(customScriptId);
      if (customTag) customTag.remove();
    };
  }, [title, description, canonicalUrl, ogType, ogImage, noindex, schema]);

  return null;
}

